import "./UhfHeader.css";

import cx from "classnames";
import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { CSSTransition, SwitchTransition } from "react-transition-group";

import { getUhfHeader, getUhfHeaderMode, UhfHeaderMode } from "@gamedev-web/gamedev-uhf";

import { RawUhfContainer } from "../RawUhfContainer";

import type { RootState } from "../../../redux/reducers/rootReducer";
// TODO: Make this a context
let refreshMeControl: (() => void) | undefined;
export const setRefreshMeControl = (refresh: () => void) => {
  refreshMeControl = refresh;
};

export const UhfHeader = () => {
  const mode: UhfHeaderMode = useSelector(getUhfHeaderMode);

  // Provides a way to opt-out of the UHF header, however, the caller is
  // responsible for compliance and accessibility
  if (mode === UhfHeaderMode.Off) {
    return null;
  }

  return <UhfHeaderRender mode={mode} />;
};

type Props = Readonly<{
  mode: UhfHeaderMode;
}>;

const UhfHeaderRender = ({ mode }: Props) => {
  const headerHtml = useSelector((state: RootState) => getUhfHeader(state));

  const refresh = useCallback(() => {
    if (refreshMeControl) {
      refreshMeControl();
      // When manually refreshing MeControl it forgets our remote configuration
      // and has trouble figuring out breakpoint for compact mode
      // This then adds a permanent horizontal scroll in most window sizes
      // Give it a little nudge to start, it then figures it out how to act on resize
      setTimeout(() => {
        if (
          globalThis.window?.innerWidth <= 1400 &&
          globalThis.window?.MeControl?.API.setDisplayMode
        ) {
          globalThis.window.MeControl.API.setDisplayMode("compact");
        }
      }, 100);
    }
  }, []);

  // If UHF is re-mounted for any reason it will put a new empty mecontrol on the page and needs to be re-initialized.
  // Timeout here is mostly for setRefreshMeControl. Once that is implemented as context, invoke it right away.
  useEffect(() => {
    const timeoutId = setTimeout(refresh, 100);
    return () => clearTimeout(timeoutId);
  }, [refresh]);

  return (
    <SwitchTransition mode={"out-in"}>
      <CSSTransition
        key={"light"}
        in={true}
        timeout={{ appear: 300, enter: 300, exit: 100 }}
        classNames="uhf-header"
        // when theme changes, it renders a different header HTML, which requires
        // re-initialization of the me control. Use onEntered() callback to ensure
        // that the new UHF Header is on the DOM before attempting to update me
        // control
        onEntered={refresh}
      >
        <RawUhfContainer
          htmlContent={headerHtml}
          className={cx(
            "uhf-header",
            { ["uhf-dark"]: false },
            { ["static-header"]: true },
            `uhf-header-mode-${mode.toLowerCase()}`
          )}
        />
      </CSSTransition>
    </SwitchTransition>
  );
};
